import controllerDataConfigApi from '@/api/config/controllerDataConfig'
import { queryKeys } from '@/constant/common'
import { LineUnit, LineUnitType } from '@/types/controller'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { resolveSourceInterfaceIds } from './resolveSourceInterfaceIds'

interface DecoderProtocolMap {
  // decoder address -> line unit protocol (1 rkd, 2 mk2, 4 mk3)
  protocolByAddress: Map<number, number>
  // False when no address resolves at all, so callers can drop the protocol
  // half of the filter instead of showing an empty list.
  hasProtocolData: boolean
}

const EMPTY: DecoderProtocolMap = {
  protocolByAddress: new Map(),
  hasProtocolData: false,
}

/**
 * Resolves each decoder's line unit protocol.
 *
 * The decoder list does not carry the protocol, so it is derived the same way
 * the API does it internally: Controller.Address -> LineUnit.Type ->
 * LineUnitType.Protocol.
 *
 * A control hub carries no line units of its own — they live on its
 * sub-controllers, each a separate interface. So the line units are read from
 * the sub-controllers when the interface is a hub, and from the interface
 * itself otherwise. (Decoder addresses do not collide across sub-controllers,
 * so a single address -> protocol map is unambiguous.)
 *
 * Interfaces without their own line unit types fall back to the mapping the
 * other interfaces agree on. When even that leaves every address unresolved,
 * `hasProtocolData` is false and callers filter on decoder type alone rather
 * than showing an empty list.
 */
function useDecoderProtocolMap(interfaceId: number) {
  const queryClient = useQueryClient()
  return useQuery<DecoderProtocolMap>(
    [queryKeys.controller, 'decoderProtocolMap', interfaceId],
    async () => {
      // A hub's line units live on its sub-controllers; read from those when
      // present, otherwise from this interface directly.
      const sourceInterfaceIds = await resolveSourceInterfaceIds(
        interfaceId,
        queryClient,
      )

      const [lineUnitResults, lineUnitTypeRes] = await Promise.all([
        Promise.all(
          sourceInterfaceIds.map((id) =>
            controllerDataConfigApi.getLineUnits(id),
          ),
        ),
        controllerDataConfigApi.getLineUnitTypes(),
      ])

      const lineUnits: LineUnit[] = lineUnitResults.flatMap(
        (res) => res?.data?.data ?? [],
      )
      const allTypes: LineUnitType[] = lineUnitTypeRes?.data?.data ?? []

      // Line unit types are defined per interface, so the source interfaces'
      // own rows win. Not every interface has them, though, and a line unit
      // type id means the same thing everywhere it is defined — id 3 is
      // "crysberg mk2" on every interface that has it. So fall back to the
      // mapping other interfaces agree on, and only skip an id when they
      // disagree.
      const preferredIds = new Set(sourceInterfaceIds.map(Number))
      const protocolByType = new Map<number, number>()
      const protocolsSeenByType = new Map<number, Set<number>>()

      allTypes.forEach((type) => {
        const id = Number(type.id)
        const protocol = Number(type.protocol)
        if (!protocol) return

        if (!protocolsSeenByType.has(id)) protocolsSeenByType.set(id, new Set())
        protocolsSeenByType.get(id)?.add(protocol)

        if (preferredIds.has(Number(type.interfaceId))) {
          protocolByType.set(id, protocol)
        }
      })

      protocolsSeenByType.forEach((protocols, id) => {
        if (protocolByType.has(id)) return
        if (protocols.size === 1) protocolByType.set(id, [...protocols][0])
      })

      const protocolByAddress = new Map<number, number>()
      lineUnits.forEach((unit) => {
        const protocol = protocolByType.get(Number(unit.type))
        if (protocol) protocolByAddress.set(Number(unit.id), protocol)
      })

      return {
        protocolByAddress,
        hasProtocolData: protocolByAddress.size > 0,
      }
    },
    {
      enabled: interfaceId > 0,
      staleTime: 5 * 60 * 1000,
      placeholderData: EMPTY,
    },
  )
}

export default useDecoderProtocolMap
